import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiBriefcaseLight, PiCheckCircleLight } from 'react-icons/pi';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import Button from '../shared/TailwindComponents/Button';
import SearchInput from '../shared/TailwindComponents/SearchInput';

const EMPTY: any[] = [];

/**
 * Contract → matter picker, rendered inside GlobalModal (custom content). Search
 * + pick a matter to link the contract to, or unlink. Mirrors the drafting matter
 * picker's look; kept contract-specific and lightweight.
 */
const ContractMatterPicker: React.FC<{
  currentMatterUuid?: string | null;
  onPick: (matterUuid: string | null) => void | Promise<void>;
  onCancel: () => void;
}> = ({ currentMatterUuid, onPick, onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const matters = useAppSelector((s: RootState) => (s.matters?.matters as any[] | undefined) ?? EMPTY);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { await dispatch(fetchMatters({ page: 1, page_size: 100, sortBy: 'created_at', sortOrder: 'desc' }) as any); } catch { /* non-fatal */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return matters.filter((m: any) => m?.uuid).filter((m: any) =>
      !term || String(m.title || '').toLowerCase().includes(term));
  }, [matters, q]);

  const pick = async (uuid: string | null) => {
    setBusy(true);
    try { await onPick(uuid); } finally { setBusy(false); }
  };

  return (
    <div className="px-4 space-y-3">
      <SearchInput value={q} onChange={(e: any) => setQ(e.target.value)} placeholder={t('Search matters…')} />
      <div className="max-h-80 overflow-y-auto custom-scrollbar-main space-y-1.5">
        {loading ? (
          <p className="py-6 text-center text-sm text-fg-muted">{t('Loading matters…')}</p>
        ) : filtered.length === 0 ? (
          <p className="py-6 text-center text-sm text-fg-muted">{t('No matters found.')}</p>
        ) : filtered.map((m: any) => {
          const selected = m.uuid === currentMatterUuid;
          return (
            <button key={m.uuid} type="button" disabled={busy} onClick={() => pick(m.uuid)}
              className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-start transition-colors ${
                selected ? 'border-champagne/50 bg-champagne/10' : 'border-hairline hover:bg-surface-sunken/60'}`}>
              <PiBriefcaseLight className="h-4 w-4 shrink-0 text-fg-muted" />
              <span className="min-w-0 flex-1 truncate text-sm text-fg-body">{m.title || t('Untitled Matter')}</span>
              {m.status_name && <span className="shrink-0 text-[11px] text-fg-muted">{m.status_name}</span>}
              {selected && <PiCheckCircleLight className="h-4 w-4 shrink-0 text-champagne" />}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between pt-1">
        {currentMatterUuid ? (
          <Button variant="outline" size="small" disabled={busy} onClick={() => pick(null)}>{t('Unlink matter')}</Button>
        ) : <span />}
        <Button variant="outline" size="small" disabled={busy} onClick={onCancel}>{t('Cancel')}</Button>
      </div>
    </div>
  );
};

export default ContractMatterPicker;
